import React from "react";
import { cn } from "../utils/cn";

export interface CheckboxOption {
  value: string;
  label: string;
  description?: string;
}

export interface CheckboxGroupProps {
  name?: string;
  options: CheckboxOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  error?: string;
  helperText?: string;
  className?: string;
  disabled?: boolean;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  name,
  options,
  selectedValues,
  onChange,
  error,
  helperText,
  className,
  disabled = false,
}) => {
  const handleToggle = (value: string) => {
    if (disabled) return;

    const isSelected = selectedValues.includes(value);
    if (isSelected) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="space-y-4">
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          return (
            <label
              key={option.value}
              className={cn(
                "flex items-center space-x-3 p-4 border-solid border-2 rounded-lg  cursor-pointer transition-all duration-200",
                "hover:bg-romance-50",
                isSelected && "border-tiber-900 bg-tiber-900/5",
                !isSelected && "border-tiber-200",
                error && "border-red-500",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <input
                type="checkbox"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => handleToggle(option.value)}
                disabled={disabled}
                className={cn(
                  "w-5 h-5 rounded border-tiber-300 text-tiber-900 outline-none checked:bg-tiber-900 focus:outline-none focus:ring-0",
                  error && "border-red-500"
                )}
              />
              <div className="flex-1">
                <div className="font-medium text-tiber-900">{option.label}</div>
                {option.description && (
                  <div className="text-sm font-medium text-tiber-800 mt-1">
                    {option.description}
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
      {error && (
        <p className="mt-1 text-sm font-medium text-red-600">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm font-medium text-tiber-800">{helperText}</p>
      )}
    </div>
  );
};

CheckboxGroup.displayName = "CheckboxGroup";
